/**
 * The little character who takes the selected desk: walks in along the aisle, sits on a chair at
 * the desk's open side and opens a laptop.
 *
 * Drawn into the injected artwork rather than over it, so the floor's own painter's order hides
 * whatever the desk should hide. Neither floor draws chairs, so the chair comes with the
 * character. Everything is placed from the desk's own top face, which works in all four rotation
 * views and on both floors without per-desk data.
 *
 * Like the artwork, the drawing carries its own colours: the clothes are the brand palette, and
 * only the laptop screen takes the map's `--accent`.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

interface Pt {
  x: number;
  y: number;
}

const add = (a: Pt, b: Pt): Pt => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Pt, b: Pt): Pt => ({ x: a.x - b.x, y: a.y - b.y });
const mul = (a: Pt, k: number): Pt => ({ x: a.x * k, y: a.y * k });
const dot = (a: Pt, b: Pt) => a.x * b.x + a.y * b.y;
const len = (a: Pt) => Math.hypot(a.x, a.y);
const unit = (a: Pt) => mul(a, 1 / (len(a) || 1));
const mid = (a: Pt, b: Pt) => mul(add(a, b), 0.5);
const n2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Maps points from one element's user space into another's. Benches sit in groups with their own
 * offsets, so a desk's numbers mean nothing outside its group until they pass through this.
 */
function mapper(from: Element, to: Element): (p: Pt) => Pt {
  const a = (from as SVGGraphicsElement).getCTM?.();
  const b = (to as SVGGraphicsElement).getCTM?.();
  if (!a || !b) return (p) => p;
  const m = b.inverse().multiply(a);
  return (p) => ({ x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f });
}

/** The desk height the figure is drawn against, in its own units. */
const DRAWN_DESK_HEIGHT = 12.3;
/** Height over desk depth on the upper floor, for a desk that draws no legs to measure. */
const HEIGHT_PER_DEPTH = 0.55;

/** A polygon's `points`, or null where any of them does not parse. */
function pointsOf(polygon: Element | null | undefined): Pt[] | null {
  const pts = polygon
    ?.getAttribute('points')
    ?.trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(',').map(Number);
      return { x: x ?? NaN, y: y ?? NaN };
    });
  return pts?.length && pts.every((p) => Number.isFinite(p.x + p.y)) ? pts : null;
}

/**
 * The space a desk is placed in: its parent layer, where the character is drawn beside it. A desk
 * group may carry its own offset (the ground floor's do), which its children's numbers leave out.
 */
const spaceOf = (desk: Element) => desk.parentElement ?? desk;

/** The four corners of a desk's top face, from its `.top` polygon, in the given space. */
function topFace(desk: Element, space: Element): Pt[] | null {
  const top = desk.querySelector('.top');
  const pts = pointsOf(top);
  return top && pts?.length === 4 ? pts.map(mapper(top, space)) : null;
}

/**
 * Whether a point, in the given space, lies on the room's floor — the first shape every view
 * paints. A chair off it would stand outside the room, behind a wall.
 */
function onFloor(view: Element, space: Element): (p: Pt) => boolean {
  const polygon = view.querySelector('polygon');
  const pts = pointsOf(polygon);
  if (!polygon || !pts) return () => true;
  const ring = pts.map(mapper(polygon, space));
  return (p) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const a = ring[i]!;
      const b = ring[j]!;
      if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
        inside = !inside;
      }
    }
    return inside;
  };
}

/**
 * How far the top sits above the floor, read off the desk's legs — or a neighbour's, for a desk
 * whose legs this view leaves out. Every desk on a floor stands the same height.
 */
function deskHeight(desk: Element, view: Element, depth: number): number {
  const leg = desk.querySelector('line') ?? view.querySelector('.desk line');
  const h = leg ? Math.abs(Number(leg.getAttribute('y2')) - Number(leg.getAttribute('y1'))) : NaN;
  return Number.isFinite(h) && h > 0 ? h : depth * HEIGHT_PER_DEPTH;
}

interface Placement {
  /** Floor point under the chair. */
  seat: Pt;
  /** Outward from the desk, along its depth. */
  out: Pt;
  /** Along the desk's length, pointing right on screen. */
  along: Pt;
  /** Middle of the desk's open edge, on the top face. */
  edge: Pt;
  /** Middle of the top face. */
  centre: Pt;
  depth: number;
  length: number;
  height: number;
}

/** A desk's top face measured out, in the space of the desk it is being compared with. */
interface Measure {
  centre: Pt;
  longV: Pt;
  shortV: Pt;
  /** Middles of the two long edges. */
  edges: [Pt, Pt];
  across: Pt;
}

function measure(desk: Element, space: Element): Measure | null {
  const top = topFace(desk, space);
  if (!top) return null;
  const [p0, p1, p2, p3] = top as [Pt, Pt, Pt, Pt];
  const e0 = sub(p1, p0);
  const e1 = sub(p2, p1);
  const [longV, shortV, edges]: [Pt, Pt, [Pt, Pt]] =
    len(e0) >= len(e1)
      ? [e0, e1, [mid(p0, p1), mid(p2, p3)]]
      : [e1, e0, [mid(p1, p2), mid(p3, p0)]];
  const centre = mul(add(add(p0, p1), add(p2, p3)), 0.25);
  return { centre, longV, shortV, edges, across: unit(shortV) };
}

/**
 * Another desk's offset from this one, in this desk's lengths along it and depths across it.
 * Measured in desk units, not screen distances: the floor's axes are not square on screen, so a
 * dot product would miss the desk straight across.
 */
function offsetOf(m: Measure, other: Measure): { sideways: number; ahead: number } {
  const { longV, shortV } = m;
  const cross = longV.x * shortV.y - longV.y * shortV.x;
  const o = sub(other.centre, m.centre);
  return {
    sideways: (o.x * shortV.y - o.y * shortV.x) / cross,
    ahead: (longV.x * o.y - longV.y * o.x) / cross,
  };
}

interface Sides {
  /** No desk facing it across the aisle. */
  open: Pt[];
  /** Floor under the chair. */
  room: Pt[];
  /** Both. */
  free: Pt[];
}

/** Which sides of a desk can take a chair, in the given space. */
function sidesOf(desk: Element, view: Element, space: Element, m: Measure): Sides {
  let partner: Pt | null = null;
  for (const other of view.querySelectorAll('.desk')) {
    if (other === desk) continue;
    const face = measure(other, space);
    if (!face) continue;
    const { sideways, ahead } = offsetOf(m, face);
    if (Math.abs(sideways) < 0.25 && Math.abs(ahead) > 0.5 && Math.abs(ahead) < 3) {
      partner = mul(m.across, ahead > 0 ? 1 : -1);
      break;
    }
  }
  const depth = len(m.shortV);
  const height = deskHeight(desk, view, depth);
  const edgeOn = (o: Pt) => m.edges.find((e) => dot(sub(e, m.centre), o) > 0) ?? m.edges[0];
  // Probed a little past the seat, so the whole chair has floor under it.
  const floor = onFloor(view, space);
  const room = (o: Pt) => floor(add(add(edgeOn(o), mul(o, depth * 0.9)), { x: 0, y: height }));
  const sides = [m.across, mul(m.across, -1)];
  const open = sides.filter((o) => !partner || dot(o, partner) < 0);
  return { open, room: sides.filter(room), free: open.filter(room) };
}

/** The desks set end to end with this one in a single run, itself left out. */
function runOf(desk: Element, view: Element, space: Element): Element[] {
  const all = [...view.querySelectorAll('.desk')].flatMap((d) => {
    const m = d.querySelector('.top') ? measure(d, space) : null;
    return m ? [{ d, m }] : [];
  });
  const run = new Set<Element>([desk]);
  const queue = all.filter(({ d }) => d === desk);
  while (queue.length) {
    const { m } = queue.shift()!;
    for (const next of all) {
      if (run.has(next.d)) continue;
      const { sideways, ahead } = offsetOf(m, next.m);
      // Lined up across, one length along: the next desk in the run, not a neighbouring run.
      if (Math.abs(ahead) < 0.25 && Math.abs(Math.abs(sideways) - 1) < 0.25) {
        run.add(next.d);
        queue.push(next);
      }
    }
  }
  run.delete(desk);
  return [...run];
}

/**
 * Where the chair goes: on whichever side of the desk is open floor. Benches pair desks back to
 * back, so it is the side away from the desk facing this one; a desk against a wall is sat at from
 * the room, never from beyond the wall. Which side that is on screen changes with the rotation,
 * so it is worked out afresh in every view.
 *
 * A desk free on both sides sits the way the rest of its run does, so a run reads as one row of
 * sitters: at 0°, desks 21 and 22 on the ground floor can only be sat at from behind, and desk 23
 * at the end of the run, with the hallway in front of it, follows them.
 */
function place(desk: Element, view: Element): Placement | null {
  const space = spaceOf(desk);
  const m = measure(desk, space);
  if (!m) return null;
  const { centre, longV, shortV, edges, across } = m;
  const length = len(longV);
  const depth = len(shortV);
  const along = longV.x >= 0 ? unit(longV) : mul(unit(longV), -1);
  const height = deskHeight(desk, view, depth);
  const edgeOn = (o: Pt) => edges.find((e) => dot(sub(e, centre), o) > 0) ?? edges[0];

  const { open, room, free } = sidesOf(desk, view, space, m);
  let preferred = free;
  if (free.length > 1) {
    const mates = runOf(desk, view, space).map((d) => {
      const mate = measure(d, space);
      return mate ? sidesOf(d, view, space, mate).free : [];
    });
    const shared = free.filter((o) => mates.every((s) => s.some((side) => dot(side, o) > 0)));
    if (shared.length) preferred = shared;
  }
  const out = preferred[0] ?? room[0] ?? open[0] ?? (across.y >= 0 ? across : mul(across, -1));

  const edge = edgeOn(out);
  return {
    seat: add(add(edge, mul(out, depth * 0.5)), { x: 0, y: height }),
    out,
    along,
    edge,
    centre,
    depth,
    length,
    height,
  };
}

/* ------------------------------------------------------------------ drawing ---
 * In figure units: the origin is the floor under the seat, y runs up the screen
 * negative, and the desk top stands DRAWN_DESK_HEIGHT above the floor. Two
 * figures cover the four iso headings — facing the viewer down-left, and facing
 * away up-left — and each is mirrored for the other two.
 * ----------------------------------------------------------------------------- */

// The clothes are the MDS Brand Guidelines palette (2026 / V0.1), the same values as Tier 1 in
// primitives.css. Written out rather than read as tokens because, like the rest of the artwork,
// the drawing carries its own colours (see the seat-map exception in CLAUDE.md).
const SKIN = '#f0c3a0';
const HAIR = '#3b2a22';
/** "Mint" [book 2.7], the signature brand colour. */
const SHIRT = '#5bf6c3';
/** "Mint Dark" [book 2.7] laid over the mint, for the side of the shirt in shadow. */
const SHADE = 'rgba(22,139,112,.45)';
/** Neutral 700 [book 2.8]. */
const TROUSERS = '#1c2025';
/** Neutral 900 [book 2.8]. */
const SHOE = '#020205';

/** The five spoke tips of the base, round an iso ellipse on the floor, the first towards the viewer. */
const SPOKES: Pt[] = Array.from({ length: 5 }, (_, i) => {
  const a = Math.PI / 2 + (i * 2 * Math.PI) / 5;
  return { x: n2(Math.cos(a) * 6), y: n2(Math.sin(a) * 3) };
});
/** One spoke, sloping from the hub down to its castor. */
const spoke = ({ x, y }: Pt) => `
  <path d="M0 -1.6 L${x} ${n2(y - 1.1)}" stroke="#2a2a2e" stroke-width="1.3" stroke-linecap="round"/>
  <ellipse cx="${x}" cy="${n2(y - 0.5)}" rx="1.15" ry="0.85" fill="#141416"/>`;

/** An office chair's undercarriage: castors, five-star base and gas lift. Far spokes go behind the column. */
const chairBase = `
  <ellipse cx="0" cy="0.2" rx="7.2" ry="3.5" fill="rgba(35,32,24,.16)"/>
  ${SPOKES.filter((p) => p.y < 0)
    .map(spoke)
    .join('')}
  <rect x="-0.75" y="-6.6" width="1.5" height="5.2" fill="#9aa0a8"/>
  <rect x="-0.75" y="-6.6" width="0.55" height="5.2" fill="#c8cdd3"/>
  <ellipse cx="0" cy="-1.5" rx="1.5" ry="0.75" fill="#2a2a2e"/>
  ${SPOKES.filter((p) => p.y >= 0)
    .map(spoke)
    .join('')}`;
/** The padded seat, with its tilt mechanism showing underneath. */
const chairSeat = `
  <rect x="-1.8" y="-4.4" width="3.6" height="1.2" rx="0.4" fill="#1f1f1d"/>
  <polygon points="-6,-7.2 0,-10.2 6,-7.2 0,-4.2" fill="#4a4a52"/>
  <polygon points="-6,-7.2 0,-4.2 0,-2.9 -6,-5.9" fill="#2e2e33"/>
  <polygon points="6,-7.2 0,-4.2 0,-2.9 6,-5.9" fill="#26262a"/>
  <polygon points="-4.2,-7.3 0,-9.4 4.2,-7.3 0,-5.2" fill="#56565f"/>`;
/** Behind a figure facing the viewer: a rounded back on a post, up-right of the seat. */
const chairBackFar = `
  <path d="M3 -8.4 L3 -12.2" stroke="#2a2a2e" stroke-width="1.3"/>
  <path d="M0 -12.6 L6 -9.6 L6 -17.4 C6 -19.9 0 -23.1 0 -20.4 Z" fill="#3a3a40"/>
  <path d="M0.9 -13.6 L5.1 -11.5 L5.1 -17.3 C5.1 -19 0.9 -21.2 0.9 -19.4 Z" fill="#44444c"/>`;
/** In front of a figure facing away: the same back, down-right of the seat, seen from behind. */
const chairBackNear = `
  <path d="M3 -5.7 L3 -8.4" stroke="#2a2a2e" stroke-width="1.3"/>
  <path d="M0 -6.8 L6 -9.8 L6 -18 C6 -20.5 0 -17.7 0 -15.2 Z" fill="#3a3a40"/>
  <path d="M6 -9.8 L6.9 -9.4 L6.9 -17.6 L6 -18 Z" fill="#26262a"/>`;

const limb = (d: string, colour: string, width: number) =>
  `<path d="${d}" stroke="${colour}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;

/** Seated, facing down-left towards a desk in front of the viewer. */
const sitFacing = `
  ${limb('M2.4 -7.6 L-2.6 -5 L-2.6 -0.6', TROUSERS, 3.2)}
  ${limb('M0.4 -8.4 L-4.8 -5.8 L-4.8 -1', TROUSERS, 3.2)}
  <ellipse cx="-3.6" cy="-0.2" rx="1.9" ry="1" fill="${SHOE}"/>
  <ellipse cx="-5.8" cy="-0.6" rx="1.9" ry="1" fill="${SHOE}"/>
  <rect x="-3.6" y="-18.4" width="7.2" height="11" rx="3" fill="${SHIRT}"/>
  <rect x="0.6" y="-18.4" width="3" height="11" rx="1.5" fill="${SHADE}"/>
  ${limb('M2.2 -15.6 L-1.8 -12.4 L-5.8 -12.8', SHIRT, 2.4)}
  ${limb('M-2.4 -15.8 L-5.4 -12.6 L-8.6 -13.2', SHIRT, 2.4)}
  <circle cx="-5.9" cy="-12.9" r="1.1" fill="${SKIN}"/>
  <circle cx="-8.8" cy="-13.3" r="1.1" fill="${SKIN}"/>
  <circle cx="-0.4" cy="-22.4" r="4" fill="${SKIN}"/>
  <path d="M-4.4 -22.6 A4.2 4.2 0 0 1 3.9 -23.4 L3.7 -20.4 Q3 -22.4 1 -23.6 Q-1.6 -24.6 -4.4 -22.6 Z" fill="${HAIR}"/>
  <circle cx="-2.6" cy="-21.9" r="0.5" fill="${SHOE}"/>
  <circle cx="-0.6" cy="-21.4" r="0.5" fill="${SHOE}"/>`;

/** Seated, facing up-left towards a desk further into the view: we see the back. */
const sitAway = `
  <rect x="-3.6" y="-18.4" width="7.2" height="11" rx="3" fill="${SHIRT}"/>
  <rect x="-3.6" y="-18.4" width="3" height="11" rx="1.5" fill="${SHADE}"/>
  ${limb('M-2.6 -15.6 L-5.6 -13.4 L-7.4 -15.4', SHIRT, 2.4)}
  <rect x="-1.4" y="-19.6" width="2.6" height="2" fill="${SKIN}"/>
  <circle cx="-3.9" cy="-21.8" r="0.95" fill="${SKIN}"/>
  <circle cx="-0.2" cy="-22.4" r="4" fill="${HAIR}"/>`;

/**
 * The same sitter's legs, reaching forward under the desk. Further from the viewer than the
 * seat, so they go in before it and show either side of it.
 */
const sitAwayLegs = `
  ${limb('M2.4 -9 L-2.6 -11.4 L-3 -3.8', TROUSERS, 3.2)}
  ${limb('M0.8 -8.2 L-4.2 -10.6 L-4.6 -3', TROUSERS, 3.2)}
  <ellipse cx="-3.8" cy="-3.4" rx="1.9" ry="1" fill="${SHOE}"/>
  <ellipse cx="-5.4" cy="-2.6" rx="1.9" ry="1" fill="${SHOE}"/>`;

/** Standing, mid-stride. The legs swing from the hip, which is the group's origin. */
const walkFacing = (away: boolean) => `
  <g transform="translate(0 -11)">
    <g class="occupant__leg">${limb('M0 0 L0 10.4', TROUSERS, 3)}</g>
    <g class="occupant__leg occupant__leg--back">${limb('M1.6 0 L1.6 10.4', TROUSERS, 3)}</g>
  </g>
  <g class="occupant__bob">
    <rect x="-3.6" y="-21.6" width="7.2" height="11.6" rx="3" fill="${SHIRT}"/>
    <rect x="${away ? -3.6 : 0.6}" y="-21.6" width="3" height="11.6" rx="1.5" fill="${SHADE}"/>
    ${limb('M-2.6 -18.6 L-3.4 -12.6', SHIRT, 2.4)}
    <circle cx="-0.3" cy="-25.6" r="4" fill="${away ? HAIR : SKIN}"/>
    ${
      away
        ? ''
        : `<path d="M-4.4 -25.8 A4.2 4.2 0 0 1 3.9 -26.6 L3.7 -23.6 Q3 -25.6 1 -26.8 Q-1.6 -27.8 -4.4 -25.8 Z" fill="${HAIR}"/>
           <circle cx="-2.5" cy="-25.1" r="0.5" fill="${SHOE}"/>`
    }
  </g>`;

/** Where on screen a heading points: down-left, down-right, up-left or up-right. */
const facing = (v: Pt) => ({ away: v.y < 0, mirrored: v.x > 0 });

/** The `matrix()` that performs a mapping between two spaces. */
function transformOf(map: (p: Pt) => Pt): string {
  const o = map({ x: 0, y: 0 });
  return frame(sub(map({ x: 1, y: 0 }), o), sub(map({ x: 0, y: 1 }), o), o);
}

/** One `matrix()` that maps unit x and y onto two screen vectors from an origin. */
const frame = (x: Pt, y: Pt, o: Pt) =>
  `matrix(${n2(x.x)} ${n2(x.y)} ${n2(y.x)} ${n2(y.y)} ${n2(o.x)} ${n2(o.y)})`;

function svg(markup: string, className: string, transform: string): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('class', className);
  g.setAttribute('transform', transform);
  g.setAttribute('aria-hidden', 'true');
  g.setAttribute('pointer-events', 'none');
  g.innerHTML = markup;
  return g;
}

/**
 * The desk's furniture alone — top, edges, legs — to lay over a character sat beyond it. Keeps its
 * state, so it shows booked or selected like the original, but not its id: nothing that looks
 * desks up by id may find the copy.
 */
function deskCopy(desk: Element): Element {
  const copy = desk.cloneNode(true) as Element;
  copy.removeAttribute('id');
  copy.removeAttribute('data-desk-id');
  copy.setAttribute('pointer-events', 'none');
  copy.querySelectorAll('.badge, .desk-hitbox, title').forEach((el) => el.remove());
  return copy;
}

/**
 * Seats a character at the desk in the shown view and returns a function that takes them away.
 * `animate` false draws them already seated — for a rotation of a desk already taken.
 */
export function seatOccupant(view: Element, deskId: string, animate: boolean): (() => void) | null {
  const desk = [...view.querySelectorAll(`.desk[data-desk-id="${deskId}"]`)].find((d) =>
    d.querySelector('.top'),
  );
  if (!desk) return null;
  const at = place(desk, view);
  if (!at) return null;

  const k = at.height / DRAWN_DESK_HEIGHT;
  const cls = animate ? 'occupant' : 'occupant occupant--still';
  // The sitter faces the desk, so back towards it from the seat.
  const sits = facing(mul(at.out, -1));
  const walks = facing(at.along);
  const scale = (mirrored: boolean) => `scale(${n2(mirrored ? -k : k)} ${n2(k)})`;
  // Steps in along the aisle from the left of the screen, a desk and a bit away.
  const walkFrom = mul(at.along, -at.length * 1.3);

  const person = svg(
    `${chairBase}
     ${sits.away ? `<g class="occupant__sitter">${sitAwayLegs}</g>` : chairBackFar}
     ${chairSeat}
     <g class="occupant__sitter">${sits.away ? sitAway : sitFacing}</g>
     ${sits.away ? chairBackNear : ''}`,
    cls,
    `translate(${n2(at.seat.x)} ${n2(at.seat.y)}) ${scale(sits.mirrored)}`,
  );
  // The walker is placed on its own so its heading can differ from the sitter's.
  const walker = svg(
    `<g class="occupant__walker" style="--walk-x:${n2(walkFrom.x)}px;--walk-y:${n2(walkFrom.y)}px">
       <g transform="${scale(walks.mirrored)}">${walkFacing(walks.away)}</g>
     </g>`,
    cls,
    `translate(${n2(at.seat.x)} ${n2(at.seat.y)})`,
  );

  // The laptop lies in the middle of the top face, its hinge on the far side so it opens towards
  // the sitter. Sized to the figure, not the desk: about as wide as the shoulders.
  const inward = mul(at.out, -1);
  const half = 3.8 * k;
  const reach = 2.6 * k;
  const base = at.centre;
  const hinge = add(base, mul(inward, reach));
  const up = add({ x: 0, y: -5.2 * k }, mul(inward, 0.9 * k));
  // The screen shows when it faces the viewer: the sitter is nearer the front than the desk.
  const screenShows = sits.away;
  const laptop = svg(
    `<g transform="${frame(mul(at.along, half), mul(at.out, reach), base)}">
       <polygon points="-1,-1 1,-1 1,1 -1,1" fill="#9aa0a6"/>
       <polygon points="-0.85,-0.8 0.85,-0.8 0.85,0.55 -0.85,0.55" fill="#7d838a"/>
     </g>
     <g transform="${frame(mul(at.along, half), up, hinge)}">
       <g class="occupant__lid">
         <polygon points="-1,0 1,0 1,1 -1,1" fill="${screenShows ? '#2b2f36' : '#b4b9bf'}"/>
         ${
           screenShows
             ? '<polygon class="occupant__screen" points="-0.85,0.12 0.85,0.12 0.85,0.88 -0.85,0.88" fill="var(--accent)"/>'
             : '<circle cx="0" cy="0.55" r="0.12" fill="#eef1f3"/>'
         }
       </g>
     </g>`,
    `${cls} occupant--laptop`,
    '',
  );

  // Painter's order; everything above is in the desk's parent space. The character goes in over
  // the whole room — every desk and piece of furniture, a closet included — and under only the
  // run labels and pins, wrapped in the offsets the view does not carry. One sat beyond the desk
  // still has it in front of them: where a desk is drawn whole in its own group, number and all,
  // with nothing laid on it (the ground floor), a copy of the desk goes in over them to hide their
  // lap. Where planters and screens are drawn over the desks (the upper floor), a copy would paint
  // over those, so the character goes in just before the desk instead — that floor has no
  // furniture to stand behind. The laptop lies on the
  // top, under the desk's number while it shows, so the desk still reads as the one picked; over
  // everything once a booking hides the number.
  const space = spaceOf(desk);
  const front = svg('', 'occupant-host', transformOf(mapper(space, view)));
  const badge = view.querySelector<SVGElement>(`.desk[data-desk-id="${deskId}"] .badge`);
  const added: Element[] = [front, laptop];

  const drawnWhole = desk.querySelector(':scope > .badge') !== null;
  if (sits.away) front.append(walker, person);
  else if (drawnWhole) front.append(walker, person, deskCopy(desk));
  else {
    desk.before(walker, person);
    added.push(walker, person);
  }
  if (badge?.parentElement && badge.style.display !== 'none') {
    const under = svg('', 'occupant-host', transformOf(mapper(space, badge.parentElement)));
    under.append(laptop);
    badge.before(under);
    added.push(under);
  } else front.append(laptop);

  // In the order they are seated, which is the order they paint.
  const labels = view.querySelector(':scope > .run-labels, :scope > .pins');
  if (!front.hasChildNodes()) front.remove();
  else if (labels) labels.before(front);
  else view.append(front);

  return () => added.forEach((el) => el.remove());
}
